// Analytics.js
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import './Analytics.css';

const VIEWS = ['week', 'month', 'year'];

const monIdx = (jsDay) => (jsDay + 6) % 7;
function startOfWeek(d0 = new Date()) { const d = new Date(d0); d.setHours(12,0,0,0); d.setDate(d.getDate() - monIdx(d.getDay())); return d; }
function endOfWeek(d0) { const s = startOfWeek(d0); const e = new Date(s); e.setDate(s.getDate() + 6); return e; }
function startOfMonth(d0 = new Date()) { return new Date(d0.getFullYear(), d0.getMonth(), 1, 12); }
function endOfMonth(d0 = new Date()) { return new Date(d0.getFullYear(), d0.getMonth() + 1, 0, 12); }
function startOfYear(d0 = new Date()) { return new Date(d0.getFullYear(), 0, 1, 12); }
function endOfYear(d0 = new Date()) { return new Date(d0.getFullYear(), 11, 31, 12); }
const ymd = (d) => d.toISOString().slice(0,10);

// --- make a skeleton with the correct number of slots for the target view ---
function buildSkeleton(fromStr, toStr, bucket) {
  const out = [];
  const from = new Date(fromStr + 'T12:00:00');
  const to   = new Date(toStr   + 'T12:00:00');

  if (bucket === 'day') {
    for (const d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      out.push({ date: ymd(d), value: 0 });
    }
  } else {
    // months
    const d = new Date(from.getFullYear(), from.getMonth(), 1, 12);
    while (d <= to) {
      out.push({ date: ymd(d), value: 0 });
      d.setMonth(d.getMonth() + 1);
    }
  }
  return out;
}

/* -------------------- Mini chart -------------------- */
function MiniBarChart({ data, height = 200, labelFormatter, animateKey, staggerMs = 30 }) {
  const H = height;
  const BASELINE = H - 28;
  const n = Math.max(1, data.length);
  const slot = 100 / n;
  const barPct = slot * 0.6;
  const offsetPct = (slot - barPct) / 2;

  const max = Math.max(1, ...data.map(d => Number(d.value) || 0));

  return (
    <svg className="minibar" width="100%" height={H} aria-label="chart">
      <line x1="0%" y1={BASELINE} x2="100%" y2={BASELINE} className="grid" />

      {/* ticks NEVER unmount; they follow the skeleton length */}
      <g className="ticks">
        {data.map((d, i) => (
          <text key={`t${d.date}`} x={`${i * slot + slot / 2}%`} y={H - 8} className="tick">
            {labelFormatter ? labelFormatter(d, i) : ''}
          </text>
        ))}
      </g>

      {/* bars remount to animate; axis/ticks stay */}
      <g className="bars" key={animateKey}>
        {data.map((d, i) => {
          const v = Number(d.value) || 0;
          const barH = Math.max(0, (BASELINE - 12) * (v / max));
          const y = BASELINE - barH;
          const xLeftPct = i * slot + offsetPct;
          return (
            <rect
              key={`b${d.date}`}
              x={`${xLeftPct}%`}
              y={y}
              width={`${barPct}%`}
              height={barH}
              rx="8"
              className="bar"
              style={{ animationDelay: `${i * staggerMs}ms` }}
            />
          );
        })}
      </g>
    </svg>
  );
}

export default function Analytics({ initialView = 'week', chartHeight = 220, className = '', style = {} }) {
  const [view, setView] = useState(VIEWS.includes(initialView) ? initialView : 'week');
  const [series, setSeries] = useState([]);    // always the current skeleton/filled series
  const [total, setTotal] = useState(0);
  const [rev, setRev] = useState(0);           // bump to re-animate bars only when data is filled

  const { from, to, bucket } = useMemo(() => {
    const now = new Date();
    if (view === 'week')  return { from: ymd(startOfWeek(now)), to: ymd(endOfWeek(now)), bucket: 'day' };
    if (view === 'month') return { from: ymd(startOfMonth(now)), to: ymd(endOfMonth(now)), bucket: 'day' };
    return { from: ymd(startOfYear(now)), to: ymd(endOfYear(now)), bucket: 'month' };
  }, [view]);

  // Seed skeleton immediately when view (or its range) changes → no tick count jump
  useEffect(() => {
    setSeries(buildSkeleton(from, to, bucket));
  }, [from, to, bucket]);

  // Fill values asynchronously; animate bars once when data arrives
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api(`/me/analytics/activity?from=${from}&to=${to}&bucket=${bucket}`);
        if (!alive || !res?.ok) return;

        const byDate = new Map((res.series || []).map(r => [r.date, Number(r.value) || 0]));
        setTotal(Number(res.totalDays) || 0);

        setSeries(prev => prev.map(p => ({ ...p, value: byDate.get(p.date) ?? 0 })));
        setRev(r => r + 1); // trigger bar animation (ticks unchanged)
      } catch {/* keep skeleton */}
    })();
    return () => { alive = false; };
  }, [from, to, bucket]);

  const caption =
    view === 'week'  ? 'Days exercised this week' :
    view === 'month' ? 'Days exercised this month' :
                       'Days exercised this year';

  const labelFormatter = useMemo(() => (d) => {
    const dt = new Date(d.date);
    if (view === 'week')  return dt.toLocaleDateString(undefined, { weekday: 'short' });
    if (view === 'month') {
      const day = dt.getDate();
      return ((day - 1) % 7 === 0) ? `${day}.` : '';   // 1., 8., 15., 22., 29.
    }
    return dt.toLocaleString(undefined, { month: 'short' });
  }, [view]);

  return (
    <div className={`panel panel--stretch analytics-card ${className}`} style={{ '--chart-h': `${chartHeight}px`, ...style }}>
      <div className="analytics-header compact">
        <div className="kpi-inline">
          <span className="kpi-value">{total}</span>
          <span className="kpi-label">{caption}</span>
        </div>

        <div className="seg top">
          {VIEWS.map(v => (
            <button key={v} className={`seg-btn ${view === v ? 'active' : ''}`} onClick={() => setView(v)}>
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="analytics-chart">
        <MiniBarChart
          data={series}
          height={chartHeight}
          labelFormatter={labelFormatter}
          animateKey={`${view}-${rev}`}   // bars animate when data fills; ticks stay still
        />
      </div>
    </div>
  );
}